import {
	NewPageType,
	SessionEnvironmentContextInput,
	SponsoredRecommendationsInput,
	SraPlacement,
	SraPlacementContext,
} from '@/@types/graphql-types';
import type { InjectionKey } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { SingleTitleSR } from '@/features/sponsored-recommendations/types';

import { computed, onServerPrefetch, provide, ref, watch } from 'vue';
import { toValue } from 'vue';

import { ObjectType, SraFormat } from '@/@types/graphql-types';
import { Platform } from '@/@types/graphql-types';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';

import { TrackingAppId, ApplicationContext } from '@/helpers/tracking/tracking-helper';
import { getSrDebuggerInfo } from '@/features/sponsored-recommendations/helpers/sponsoredrec-debugging-tools';
import { captureMessageForSentry } from '@/libraries/sentry';

import {
	SendSraBidDocument,
	SendSraBidMutation,
	SendSraBidMutationVariables,
} from '@/features/sponsored-recommendations/graphql/mutations/SendSraBid.mutation';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useSponsoredRecommendationsStore } from '@/stores/pinia/useSponsoredRecommendationsStore';
import { SessionEnvironment, useTrackingStore } from '@/stores/pinia/useTrackingStore';

import AboveBuyboxCampaignsConfig from '../config/HACK-above-buybox-campaign.json';

export type SRAdType = 'POSTER' | 'CINEMA' | 'LIST' | 'VIDEO';
export type SRPageTypes =
	| 'VIEW_TITLE_DETAIL'
	| 'VIEW_POPULAR'
	| 'VIEW_HOME_USER'
	| 'VIEW_HOME_GLOBAL'
	| 'VIEW_SEARCH_SUGGESTER'
	| 'VIEW_SEARCH'
	| 'VIEW_BUYBOX'
	| NewPageType;
export type SRCatalogueKey = `${SraPlacement}-${SRPageTypes}`;

interface UseSponsoredRecOptions {
	pageType: SRPageTypes;
	placement: SraPlacement;
	/** Seo Flag for SQTV SRs  */
	isSeo?: boolean;
	testingMode?: boolean;
	alwaysReturnBidID?: boolean;
	jwEntityID?: MaybeRefOrGetter<SraPlacementContext['jwEntityID'] | undefined>;
	makeFreshBid?: boolean;
	isEnabled?: MaybeRefOrGetter<boolean>;
	buyBoxType?: NewBuyBoxView;
}

export const SR_PROVIDER_KEY = Symbol() as InjectionKey<SRCatalogueKey>;

const supportedFormats = [SraFormat.Image, SraFormat.Video];
const supportedObjectTypes = [ObjectType.Movie, ObjectType.Show, ObjectType.GenericTitleList, ObjectType.ShowSeason];

/** Standalone logic to get a Sponsored Recommendation. */
export function useSendBidId({
	pageType,
	placement,
	jwEntityID,
	testingMode = false,
	alwaysReturnBidID = true,
	isEnabled = true,
	isSeo = false,
	buyBoxType,
}: UseSponsoredRecOptions) {
	const trackingStore = useTrackingStore();
	const { language, country } = storeToRefs(useLanguageStore());
	const srStore = useSponsoredRecommendationsStore();
	const { setSponsoredAd } = srStore;

	const isSRLoaded = ref(false);
	const mutationPromise = ref<Promise<any> | null>(null);

	// Set the key
	const srCatalogueKey: SRCatalogueKey = `${placement}-${pageType}`;
	provide(SR_PROVIDER_KEY, srCatalogueKey);

	// Get the debugging information to override campaign settings
	const srDebuggerInfo = getSrDebuggerInfo();

	const sessionEnvironmentContext = computed<SessionEnvironmentContextInput>(() => {
		const {
			// fields not in mutation
			sessionReady,
			originRefrSocial,

			// fields to trim
			origin,
			originPageUrl,
			originPageReferred,

			// fields to rename
			hasAdBlocker: hasAdblocker,

			...sessionEnvironment
		} = trackingStore.sessionEnvironment as SessionEnvironment;

		void sessionReady;
		void originRefrSocial;

		return {
			...sessionEnvironment,
			hasAdblocker,
			origin: trimForTracking(origin),
			originPageURL: trimForTracking(originPageUrl),
			originPageReferred: trimForTracking(originPageReferred),
		};
	});

	// Create Mutation Inputs, with debugging information
	const sraInput = computed<SponsoredRecommendationsInput>(() => ({
		// Campaign specific information
		pageType: toValue(pageType),
		placement: toValue(placement),

		// Default settings
		language: language.value,
		country: country.value,
		applicationContext: {
			...ApplicationContext,
			...(buyBoxType && { extras: JSON.stringify({ buyboxType: buyBoxType }) }),
		},
		appId: TrackingAppId,
		platform: Platform.Web,
		sessionEnvironmentContext: sessionEnvironmentContext.value,
		isSeo,
		// Have every SR Placement supoort every format
		supportedFormats,
		supportedObjectTypes,
		alwaysReturnBidID,

		// Debugging information
		geoCountry: srDebuggerInfo?.geoCountry,
		testingModeCampaignName: srDebuggerInfo?.campaignName,
		testingModeSubcampaignId: srDebuggerInfo?.subcampaignId,
		testingModeAdID: srDebuggerInfo?.adID,
		testingModeForceHoldoutGroup: !!srDebuggerInfo?.holdoutGroup,
		testingMode: srDebuggerInfo?.testingMode || testingMode,
	}));

	// Manage Mutation Request
	const { onDone, onError, mutate } = useMutation<SendSraBidMutation, SendSraBidMutationVariables>(
		SendSraBidDocument,
		{ fetchPolicy: 'no-cache' }
	);

	onDone(data => {
		if (srCatalogueKey != null && data?.sendSraBid?.ad != null) {
			setSponsoredAd({ catalogueKey: srCatalogueKey, ad: data.sendSraBid.ad });
		}

		// Ensure SR mutation is completed
		isSRLoaded.value = true;
	});

	onError(error => {
		captureMessageForSentry(
			'[GraphQL Send Bid Request]:',
			{ error, where: 'Composable: useSendBidId', sraInput: sraInput.value },
			'error',
			{ SR: srCatalogueKey }
		);

		// Mark as loaded to allow SSR to continue gracefully without ad
		isSRLoaded.value = true;
	});

	watch(
		[
			// needed otherwise all values in there would still be null
			() => trackingStore.sessionEnvironment.sessionReady,
			() => toValue(isEnabled),
			() => toValue(jwEntityID),
		],

		([isReady, isEnabled, hasJwEntity]) => {
			/** Boolean value for the required conditions for mutation per placement type */
			const placementMutationConditionFulfilled =
				placement === SraPlacement.DetailPage ? isEnabled && hasJwEntity : isEnabled;

			// On server: Skip client-only checks for SEO placements
			// On client: Wait for all conditions to be met, but never run if isSeo=true
			const shouldMutate = import.meta.server
				? isSeo && placementMutationConditionFulfilled
				: !isSeo && isReady && placementMutationConditionFulfilled;

			if (shouldMutate) {
				// Await this promise directly in onServerPrefetch
				mutationPromise.value = mutate({
					language: language.value,
					country: country.value,
					input: {
						sraInput: sraInput.value,
						placementContext: { jwEntityID: toValue(jwEntityID) ?? undefined },
					},
				}).catch(() => {});
			}
		},
		{ immediate: true }
	);

	onServerPrefetch(async () => {
		// Automatically handle SSR prefetch when isSeo is true
		if (!isSeo) return;

		if (mutationPromise.value) {
			await mutationPromise.value;
		}
	});

	const sponsoredAd = computed(() => srStore.catalogue[srCatalogueKey] ?? null);

	return {
		isSRLoaded,
		sponsoredAd,
		...useSponsoredRec({ sponsoredAd }),
	};
}

/**
 * Return the SR campaign based upon the placement of the campaign
 *
 * @param srCatalogueKey Placement of the campaign
 */
export function getSrPlacement(srCatalogueKey: SRCatalogueKey) {
	const srStore = useSponsoredRecommendationsStore();

	return srStore.catalogue[srCatalogueKey] ?? null;
}

/**
 * Get the SR campaign and it's placement based upon the Bid ID
 *
 * @param bidId {string} The unique bid ID for that instance of the SR campaign
 */
export function getSrFromBidId(bidId: string | undefined) {
	const srStore = useSponsoredRecommendationsStore();

	const catalogue = srStore.catalogue;
	const [srCatalogueKey, sponsoredAd] = Object.entries(catalogue).find(([, ad]) => bidId === ad?.bidId) ?? [
		null,
		null,
	];

	return {
		sponsoredAd,
		placement: srCatalogueKey ? (srCatalogueKey.split('-')[0] as SraPlacement) : null,
	};
}

export function useCampaignInfo(campaign?: MaybeRefOrGetter<SponsoredAdFragment['campaign'] | undefined>) {
	const campaignNode = computed<SingleTitleSR>(() => toValue(campaign)?.node as SingleTitleSR);

	const promotionalTitle = computed(() => {
		if (toValue(campaign)?.promotionalTitle) {
			return toValue(campaign)?.promotionalTitle;
		}

		const campaignTitle = campaignNode.value.content.title;

		if (campaignNode.value?.__typename === 'Season') {
			return `${campaignNode.value.show.content.originalTitle} - ${campaignTitle}`;
		}

		return campaignTitle;
	});

	const genresList = computed(() => {
		return campaignNode.value.content.genres.map(genre => genre.translation);
	});

	const releaseYear = computed(() => campaignNode.value.content.originalReleaseYear?.toString());

	const promotionalText = computed<string | null | undefined>(() => toValue(campaign)?.promotionalText);

	const promotionalTextWithGenreFallback = computed<string>(
		() => promotionalText.value || [releaseYear.value, ...genresList.value].join(' - ')
	);

	// HACK: APPLE PLUS - display disclaimer text
	const hasDisclaimerText = computed(
		() => toValue(campaign)?.name?.toUpperCase().includes('APPLEPLUS') && toValue(campaign)?.promotionalText
	);

	return {
		campaignNode,
		promotionalTitle,
		promotionalText,
		promotionalTextWithGenreFallback,
		hasDisclaimerText,
		releaseYear,
	};
}

/** Certain string values have a BI limit of 6000 characters, this just trims them down. */
export function trimForTracking(value?: string | null, max = 1000) {
	// undefined instead of null so the value is ignore in tracking
	if (value == null) return undefined;

	return value.substring(0, max);
}

export function useSponsoredRec({
	sponsoredAd,
	objectType = null,
}: {
	sponsoredAd: MaybeRefOrGetter<SponsoredAdFragment | null>;
	objectType?: MaybeRefOrGetter<ObjectType | null>;
}) {
	const isShadowCampaign = computed(
		() => toValue(sponsoredAd)?.bidId != null && toValue(sponsoredAd)?.campaign == null
	);

	const sponsoredAdType = computed(() => toValue(sponsoredAd)?.campaign?.creativeType ?? null);

	const hasActiveSRCampaign = computed(
		() => toValue(sponsoredAd)?.holdoutGroup === false && toValue(sponsoredAd)?.campaign !== null
	);

	const isListSponsoredAd = computed(() => toValue(sponsoredAd)?.campaign?.node?.__typename === 'GenericTitleList');

	const putSRAboveBuybox = computed(() => {
		const srName = toValue(sponsoredAd)?.campaign?.name ?? null;
		const isShowPage = toValue(objectType) === ObjectType.Show;

		return (
			isShowPage &&
			!isShadowCampaign.value &&
			srName != null &&
			(AboveBuyboxCampaignsConfig as string[]).includes(srName)
		);
	});

	return {
		isShadowCampaign,
		sponsoredAdType,
		hasActiveSRCampaign,
		isListSponsoredAd,

		putSRAboveBuybox,
	};
}
